import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Listbox, Transition } from '@headlessui/react'
import PropTypes from "prop-types"
import { IconCircleCheck, IconSearch } from "@tabler/icons"
import {getStringValue} from '../../utils'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

let activePortaledClose = null

function PortaledDropdownCloser({ portaled, open, buttonRef, menuShellRef, scrollContainerRef, dropdownRef, onClose }) {
  useEffect(() => {
    if (!portaled || !open) return

    if (activePortaledClose && activePortaledClose !== onClose) {
      activePortaledClose()
    }
    activePortaledClose = onClose

    const isInsideMenu = (target) => (
      buttonRef.current?.contains(target)
      || menuShellRef.current?.contains(target)
      || scrollContainerRef.current?.contains(target)
      || dropdownRef.current?.contains(target)
    )

    const handleClickOutside = (event) => {
      if (isInsideMenu(event.target)) return
      onClose()
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('click', handleClickOutside, true)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
      document.removeEventListener('keydown', handleKeyDown)
      if (activePortaledClose === onClose) {
        activePortaledClose = null
      }
    }
  }, [portaled, open, buttonRef, menuShellRef, scrollContainerRef, dropdownRef, onClose])

  return null
}

const parseMenuWidth = (width) => {
  const parsed = parseInt(String(width), 10)
  return Number.isNaN(parsed) ? 170 : parsed
}

export const Dropdown = ({ value, callback, action, component, searchEnabled = false, options, searchValue, searchCallback, width, alignTop, alignLeft, maxHeight, autoAdjustBottom, dataAttribute, dropdownItemDataAttribute, className = '', display, backgroundColor = '', noWidth = false, offsetHeight, disabled = false, onClose = null, scrollHint = false, portaled = false, portalZIndex = 9999 }) => {

  const [optionsHeight, setOptionsHeight] = useState('')
  const [autoTop, setAutoTop] = useState(false)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })

  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const menuShellRef = useRef(null)
  const isOpenRef = useRef(false)

  const closePortaledMenu = useCallback(() => {
    if (portaled && isOpenRef.current && buttonRef.current) {
      buttonRef.current.click()
    }
  }, [portaled])

  const handleChange = useCallback((selected) => {
    callback(selected)
    if (portaled) {
      queueMicrotask(() => {
        if (isOpenRef.current) closePortaledMenu()
      })
    }
  }, [callback, portaled, closePortaledMenu])
  
  const styles = {
    global: 'font-[14px]',
    action: {
      secondary: {
        text: 'text-secondary bg-gray-400',
        underline: 'border-b-2 border-gray-800 pb-[12px]'
      },
      primary: {
        text: `text-secondary ${backgroundColor || 'bg-white'}`,
        underline: 'border-b-2 border-gray-100 pb-[12px]'
      }
    }
  }

  const handleClose = () => {
    setCanScrollUp(false)
    setCanScrollDown(false)
    if (onClose) {
      onClose();
    }
  };

  const { global, action: actionStyles } = styles
  const typeStyles = global.concat(` ${actionStyles[action].text}`)
  const disabledStyles = 'pointer-events-none opacity-50'
  const underLineStyles = global.concat(` ${actionStyles[action].underline}`)

  const updateScrollState = () => {
    if (!scrollHint) return
    const el = scrollContainerRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    const hasOverflow = scrollHeight > clientHeight + 2
    setCanScrollUp(hasOverflow && scrollTop > 2)
    setCanScrollDown(hasOverflow && scrollTop + clientHeight < scrollHeight - 2)
  }

  const scheduleScrollStateUpdate = () => {
    updateScrollState()
    requestAnimationFrame(() => {
      updateScrollState()
      requestAnimationFrame(updateScrollState)
    })
  }

  const updatePortalPosition = () => {
    if (!portaled || !buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const menuWidth = parseMenuWidth(width)
    const estimatedHeight = menuShellRef.current?.offsetHeight || 180
    let top = rect.bottom + 4
    let left = alignLeft ? rect.right - menuWidth : rect.left
    if (autoAdjustBottom && (top + estimatedHeight > window.innerHeight || (offsetHeight && rect.bottom >= offsetHeight))) {
      top = Math.max(8, rect.top - estimatedHeight - 4)
    }
    setMenuPosition({ top, left })
  }

  const beforeEnter = () => {
    if (portaled) {
      updatePortalPosition()
    } else {
      setOptionsHeight(`${buttonRef.current.clientHeight + dropdownRef.current.parentNode.offsetHeight - 30}`)
      const { bottom } = buttonRef.current?.getBoundingClientRect()
      if(bottom + dropdownRef.current?.parentNode.offsetHeight >= document.body.offsetHeight){
        setAutoTop(true)
      } else if(offsetHeight && bottom >= offsetHeight){
        setAutoTop(true)
      } else {
        setAutoTop(false)
      }
    }
    if (scrollHint) scheduleScrollStateUpdate()
  }

  const handleAfterEnter = () => {
    if (portaled) updatePortalPosition()
    if (scrollHint) scheduleScrollStateUpdate()
  }

  const renderOptions = () => (
    <>
      {searchEnabled && 
        <div className="relative text-dark-grey w-auto mx-auto mt-[16px] mb-[14px] ml-[20px] mr-[20px]">
          <span className="absolute opacity-20 left-[8px] top-[10px]">
            <IconSearch color="#021747" stroke={1} size={16}/>
          </span>
          <input 
            placeholder="Search..."
            className="border-2 border-gray-400 rounded pl-[32px] text-mm-sm pr-[12px] h-[36px] pt-[4px] pb-[4px] w-full focus:outline-none"
            value={searchValue}
            onChange={searchCallback}
          />
        </div>
      }
      {options.map((item, index) => {
        const { icon = null, iconPlacement = 'left', ctaIcon = null, ctaIconClick = null, noHoverEffect = false } = item
        return(
          <Fragment key={index}>
            {
              Object.keys(item).length > 0 &&
                <Listbox.Option
                  className={({ active }) =>
                    classNames(
                      (active && !noHoverEffect) ? 'bg-primary-lighter' : 'bg-none',
                      `select-none relative pt-[8px] pl-[20px] pr-[20px] ${item.className} ${item.hasUnderline ? underLineStyles : 'pb-[8px]'} ${item.disabled ? disabledStyles : !noHoverEffect ? 'cursor-pointer' : ''}`
                    )
                  }
                  value={item}
                >
                  <div 
                    {...(dropdownItemDataAttribute && 
                    {"data-attribute": `dropdown-item-${dropdownItemDataAttribute}-${item.value ? getStringValue(item.value) : getStringValue(item.label)}`})}
                  >
                    <div className='flex items-center'>
                      {
                        icon && iconPlacement === 'left' &&
                        <span className="pr-[16px]">
                          {icon}
                        </span>
                      }
                      <span className="text-sm w-full overflow-hidden">
                        {item.label}
                      </span>
                      {
                        icon && iconPlacement === 'right' &&
                        <span className="pl-[16px]">
                          {icon}
                        </span>
                      }
                      {
                        ctaIcon &&
                        <span className="pl-[16px]" onClick={ (e) => {  ctaIconClick() } }>
                          {ctaIcon}
                        </span>
                      }
                    </div>
                    {item.selected ? (
                      <span className={'absolute inset-y-0 right-0 flex items-center pr-[16px]'}>
                        <IconCircleCheck stroke={1.25} color={"#525C70"} size={20}/>
                      </span>
                    ) : null}
                  </div>
                </Listbox.Option>
              }
            </Fragment>
        )
      })}
    </>
  )

  const menuClassName = scrollHint
    ? `shadow-md sm:text-sm rounded-[8px] overflow-hidden ${typeStyles} ${maxHeight} flex flex-col ${portaled ? 'fixed' : 'absolute z-50'} ${className}`
    : `absolute z-50 shadow-md py-[6px] overflow-y-auto sm:text-sm rounded-[8px] ${typeStyles} ${maxHeight} ${className}`

  const menuStyle = portaled
    ? { width: `${width}`, top: `${menuPosition.top}px`, left: `${menuPosition.left}px`, zIndex: portalZIndex }
    : {
        width: `${width}`,
        ...(((autoAdjustBottom) && (alignTop || autoTop)) && { top: `-${optionsHeight}px` }),
        ...(alignLeft && { right: `0px` })
      }

  const optionsMenu = (
    <Listbox.Options
      ref={menuShellRef}
      static={portaled}
      className={menuClassName}
      style={menuStyle}
    >
      {scrollHint ? (
        <>
          <div
            ref={scrollContainerRef}
            onScroll={updateScrollState}
            onWheel={(e) => e.stopPropagation()}
            className="media-library-dropdown-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain py-[6px] pr-1"
          >
            <div ref={dropdownRef}>{renderOptions()}</div>
          </div>
          {canScrollUp && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-white via-white/80 to-transparent" aria-hidden="true" />
          )}
          {canScrollDown && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-white via-white/90 to-transparent" aria-hidden="true" />
          )}
          {canScrollDown && !canScrollUp && (
            <span className="pointer-events-none absolute inset-x-0 bottom-1.5 z-20 text-center text-[11px] font-medium text-secondary" aria-hidden="true">
              Scroll for more
            </span>
          )}
        </>
      ) : (
        <div ref={dropdownRef}>{renderOptions()}</div>
      )}
    </Listbox.Options>
  )

  const renderTransition = (open) => (
    <Transition
      show={open}
      as={Fragment}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      beforeEnter={beforeEnter}
      afterEnter={handleAfterEnter}
      afterLeave={handleClose}
    >
      {optionsMenu}
    </Transition>
  )

  return (
    <Listbox value={value} onChange={handleChange} disabled={disabled}>
      {({ open }) => {
        isOpenRef.current = open

        return (
          <>
            <PortaledDropdownCloser
              portaled={portaled}
              open={open}
              buttonRef={buttonRef}
              menuShellRef={menuShellRef}
              scrollContainerRef={scrollContainerRef}
              dropdownRef={dropdownRef}
              onClose={closePortaledMenu}
            />
            <div className={`relative ${!noWidth && 'w-full'} ${display}`} onClick={(e) => e.stopPropagation()}>
              <Listbox.Button ref={buttonRef} className="w-full" {...(dataAttribute && {"data-attribute": `dropdown-${dataAttribute}`})}>
                { component }
              </Listbox.Button>
              {portaled
                ? open && createPortal(renderTransition(open), document.body)
                : renderTransition(open)
              }
            </div>
          </>
        )
      }}
    </Listbox>
  )
}

Dropdown.propTypes = {
  callback: PropTypes.func, 
  action: PropTypes.oneOf(['secondary', 'primary']), 
  component: PropTypes.element, 
  searchEnabled: PropTypes.bool,
  options: PropTypes.array,
  searchValue: PropTypes.string,
  searchCallback: PropTypes.func,
  width: PropTypes.string,
  alignment: PropTypes.oneOf(["right", "left"]),
  alignTop: PropTypes.bool, 
  alignLeft: PropTypes.bool,
  maxHeight: PropTypes.string,
  autoAdjustBottom: PropTypes.bool,
  dataAttribute: PropTypes.string,
  dropdownItemDataAttribute: PropTypes.string,
  display: PropTypes.string,
  noWidth: PropTypes.bool,
  offsetHeight: PropTypes.number,
  disabled: PropTypes.bool,
  scrollHint: PropTypes.bool,
  portaled: PropTypes.bool,
  portalZIndex: PropTypes.number,
};

Dropdown.defaultProps = {
  action: 'primary',
  component: <div>Dropdown</div>, 
  searchEnabled: false,
  options: [
    {
      label: "Shazaib",
      value: "shahzaib"
    },
    {
      label: "Ameeq",
      value: "ameeq",
      hasUnderline: true,
      icon: <IconSearch size={16} color={"#000"} stroke={1.5}/>,
      selected: true
    },
    {
      label: "Zain",
      value: "zain",
      disabled: true
    }
  ],
  searchValue: '',
  callback: () => console.log(""),
  searchCallback: () => console.log(""),
  width: "100%",
  alignTop: false, 
  alignLeft: false,
  maxHeight: "",
  autoAdjustBottom: true,
  display:"",
  noWidth: false,
  offsetHeight: 0,
  disabled: false,
  scrollHint: false,
  portaled: false,
};
